import React from 'react';
import { Avatar, SvgIcon } from '@elementary/common-ui/components';
import { ReactComponent as FailedRunIcon } from '@elementary/common-ui/assets/16x16/failed-run.svg';
import { ReactComponent as RunSuccessIcon } from '@elementary/common-ui/assets/16x16/run-success.svg';
import { ReactComponent as RunWarningIcon } from '@elementary/common-ui/assets/16x16/run-warning.svg';
import { ReactComponent as ModelErrorIcon } from '@elementary/common-ui/assets/16x16/model-error.svg';
import { ReactComponent as ModelSuccessIcon } from '@elementary/common-ui/assets/16x16/model-success.svg';
import { ReactComponent as SyncFailureIcon } from '@elementary/common-ui/assets/16x16/sync-failure.svg';
import { ReactComponent as SyncSuccessIcon } from '@elementary/common-ui/assets/16x16/sync-success.svg';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '@elementary/common-ui/assets/16x16/teams.svg';
import { ReactComponent as PagerDutyIcon } from '@elementary/common-ui/assets/16x16/pager.svg';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/16x16/opsgenie.svg';
import { ReactComponent as EmailIcon } from '@elementary/common-ui/assets/16x16/email.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/16x16/edit-2.svg';
import { ReactComponent as EventIcon } from '@elementary/common-ui/assets/16x16/severity.svg';
import {
  AlertIntegrationType,
  EtlTool,
} from '@elementary/common-ui/services/gql/types';
import { FetchTeamUsersApi } from 'shared/types';
import { getToolDisplayName } from 'shared/components/Pipeline/utils';
import {
  formatAlertDestinationChannel,
  getAlertDestinationLabel,
  severityIconMap,
  statusIconMap,
  toSafeSlackUrl,
} from 'incidents/utils';
import { Severity, Status } from 'incidents/types';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { EventStatus, EventType, IncidentEvent } from '../../types';
import type { EventLabelResult, EventLink } from '../TimelineEventLabel';

const formatEventLabel = (
  toolType: EtlTool | undefined,
  event: string,
  status: string,
): string => {
  const toolPrefix = toolType ? `${getToolDisplayName(toolType)} ` : '';
  return `${toolPrefix}${event.replaceAll('_', ' ')}${
    status ? `: ${status}` : ''
  }`;
};

export type EventGroup = {
  events: IncidentEvent[];
  label: string;
  link?: EventLink;
  firstEvent: IncidentEvent;
  lastEvent: IncidentEvent;
};

export const formatDate = (date?: string) =>
  localizeDate(date).format('MMM D, h:mm A');

const getAlertIcon = (integrationType?: AlertIntegrationType) => {
  switch (integrationType) {
    case 'SLACK':
      return <SlackIcon />;
    case 'MICROSOFT_TEAMS':
      return <TeamsIcon />;
    case 'PAGER_DUTY':
      return <PagerDutyIcon />;
    case 'OPSGENIE':
      return <OpsgenieIcon />;
    case 'EMAIL':
      return <EmailIcon />;
    default:
      return <EmailIcon />;
  }
};

const getStatusIcon = (status?: Status) =>
  statusIconMap[status || 'Open'] &&
  React.cloneElement(statusIconMap[status || 'Open'], {
    width: 16,
    height: 16,
  });

const getSeverityIcon = (severity?: Severity) =>
  severityIconMap[severity || 'Normal'] &&
  React.cloneElement(severityIconMap[severity || 'Normal'], {
    width: 16,
    height: 16,
  });

const getTestRunIcon = (eventStatus: EventStatus) => {
  switch (eventStatus) {
    case EventStatus.SUCCESS:
      return <RunSuccessIcon />;
    case EventStatus.WARN:
      return <RunWarningIcon />;
    default:
      return <FailedRunIcon />;
  }
};

const getModelRunIcon = (eventStatus: EventStatus) => {
  switch (eventStatus) {
    case EventStatus.SUCCESS:
      return <ModelSuccessIcon />;
    default:
      return <ModelErrorIcon />;
  }
};

const getSyncRunIcon = (eventStatus: EventStatus) => {
  switch (eventStatus) {
    case EventStatus.SUCCESS:
      return <SyncSuccessIcon />;
    default:
      return <SyncFailureIcon />;
  }
};

const getAssigneeIcon = (
  assigneeEmail?: string | null,
  usersData?: FetchTeamUsersApi,
) => {
  if (!assigneeEmail) {
    return <UserIcon />;
  }
  const user = usersData?.find((u) => u.email === assigneeEmail);
  const userName = user?.name || assigneeEmail;
  return <Avatar value={{ id: user?.id || assigneeEmail, name: userName }} />;
};

export const getEventIcon = (
  event: IncidentEvent,
  usersData?: FetchTeamUsersApi,
) => {
  switch (event.eventType) {
    case EventType.TEST_RUN:
      return getTestRunIcon(event.eventStatus);
    case EventType.TEST_DELETED:
      return <FailedRunIcon />;
    case EventType.MODEL_RUN:
      return getModelRunIcon(event.eventStatus);
    case EventType.MODEL_DELETED:
      return <ModelErrorIcon />;
    case EventType.ETL_TOOL_RUN:
      return getSyncRunIcon(event.eventStatus);
    case EventType.ASSIGNEE_CHANGE:
      return getAssigneeIcon(event.data?.assignee, usersData);
    case EventType.STATUS_CHANGE:
      return getStatusIcon(event.data?.status as Status);
    case EventType.SEVERITY_CHANGE:
      return getSeverityIcon(event.data?.severity as Severity);
    case EventType.ALERT_SENT:
      return getAlertIcon(event.data?.destination?.type);
    case EventType.SUMMARY_GENERATED:
    case EventType.SUMMARY_EDITED:
    case EventType.SUMMARY_DELETED:
    case EventType.SUMMARY_SENT:
    case EventType.SUMMARY_WRITTEN:
      return <EditIcon />;
    default:
      return (
        <SvgIcon width={16}>
          <EventIcon />
        </SvgIcon>
      );
  }
};

const formatChangedBy = (changerName?: string): string =>
  changerName ? ` by ${changerName}` : '';

const formatValue = (value?: string | null): string =>
  value ? ` to '${value}'` : '';

const getUserNameByEmail = (
  email: string | null | undefined,
  usersData?: FetchTeamUsersApi,
): string | undefined => {
  if (!email || !usersData) return undefined;
  const user = usersData.find((u) => u.email === email);
  return user?.name;
};

export const getEventLabel = (
  event: IncidentEvent,
  testName?: string,
  modelName?: string,
  usersData?: FetchTeamUsersApi,
): EventLabelResult => {
  const { data } = event;
  const changerName = data?.changer_name;
  const assigneeName = getUserNameByEmail(data?.assignee, usersData);

  const formattedTestName = testName ? `'${testName}'` : 'Test';
  const formattedModelName = modelName ? `'${modelName}'` : 'Model';

  switch (event.eventType) {
    case EventType.TEST_RUN:
      return {
        label:
          event.eventStatus === EventStatus.FAIL
            ? formattedTestName + ' test result: Failure'
            : event.eventStatus === EventStatus.WARN
            ? formattedTestName + ' test result: Warning'
            : formattedTestName + ' test result: Success',
      };
    case EventType.TEST_DELETED:
      return { label: formattedTestName + ' deleted' };
    case EventType.MODEL_RUN:
      return {
        label:
          event.eventStatus === EventStatus.FAIL
            ? formattedModelName + ' model error'
            : formattedModelName + ' model success',
      };
    case EventType.MODEL_DELETED:
      return { label: formattedModelName + ' deleted' };
    case EventType.ETL_TOOL_RUN: {
      const toolType = event.data?.tool_type;
      const eventName = event.data?.event ?? 'sync';
      return {
        label: formatEventLabel(toolType, eventName, event.eventStatus),
      };
    }
    case EventType.ASSIGNEE_CHANGE: {
      const value = formatValue(assigneeName || data?.assignee);
      if (value) {
        return { label: 'Assigned' + value + formatChangedBy(changerName) };
      }
      return { label: 'Assignee removed' + formatChangedBy(changerName) };
    }
    case EventType.STATUS_CHANGE: {
      const viaJira = data?.source === 'jira_webhook' ? ' via Jira' : '';
      return {
        label:
          'Status changed' +
          formatValue(data?.status) +
          viaJira +
          formatChangedBy(changerName),
      };
    }
    case EventType.SEVERITY_CHANGE:
      return {
        label:
          'Severity changed' +
          formatValue(data?.severity) +
          formatChangedBy(changerName),
      };
    case EventType.ALERT_SENT: {
      const destination = data?.destination;
      const destinationLabel = getAlertDestinationLabel(destination?.type);
      const channelInfo = formatAlertDestinationChannel(destination);
      const safeSlackUrl = toSafeSlackUrl(data?.slack_permalink);
      const link: EventLink | undefined =
        destination?.type === 'SLACK' && safeSlackUrl && destination.channel
          ? {
              url: safeSlackUrl,
              tooltip: 'Open in Slack',
              linkedText: `#${destination.channel}`,
            }
          : undefined;
      return {
        label: 'Alert sent' + destinationLabel + channelInfo,
        link,
      };
    }
    case EventType.SUMMARY_GENERATED:
      return { label: 'Summary generated' };
    case EventType.SUMMARY_EDITED:
      return { label: 'Summary edited' + formatChangedBy(changerName) };
    case EventType.SUMMARY_DELETED:
      return { label: 'Summary deleted' + formatChangedBy(changerName) };
    case EventType.SUMMARY_SENT:
      return { label: 'Summary sent' };
    case EventType.SUMMARY_WRITTEN:
      return { label: 'Summary written' + formatChangedBy(changerName) };
    default:
      return { label: event.eventType };
  }
};

export const getEventLink = (event: IncidentEvent): EventLink | undefined => {
  if (event.eventType !== EventType.ALERT_SENT) return undefined;
  const destination = event.data?.destination;
  const safeSlackUrl = toSafeSlackUrl(event.data?.slack_permalink);
  if (destination?.type !== 'SLACK' || !safeSlackUrl || !destination.channel) {
    return undefined;
  }
  return {
    url: safeSlackUrl,
    tooltip: 'Open in Slack',
    linkedText: `#${destination.channel}`,
  };
};

export const groupConsecutiveEvents = (
  sortedEvents: IncidentEvent[],
  testName?: string,
  modelName?: string,
  usersData?: FetchTeamUsersApi,
): EventGroup[] => {
  if (sortedEvents.length === 0) return [];

  const groups: EventGroup[] = [];
  let currentGroup: IncidentEvent[] = [sortedEvents[0]];
  let current = getEventLabel(sortedEvents[0], testName, modelName, usersData);

  for (let i = 1; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    const result = getEventLabel(event, testName, modelName, usersData);

    if (result.label === current.label) {
      currentGroup.push(event);
    } else {
      groups.push({
        events: currentGroup,
        label: current.label,
        link: current.link,
        firstEvent: currentGroup[0],
        lastEvent: currentGroup[currentGroup.length - 1],
      });
      currentGroup = [event];
      current = result;
    }
  }

  groups.push({
    events: currentGroup,
    label: current.label,
    link: current.link,
    firstEvent: currentGroup[0],
    lastEvent: currentGroup[currentGroup.length - 1],
  });

  return groups;
};
